import { Button } from '@/components/UI/Button'
import { Typography } from '@/components/UI/Typography'
import { IconLock } from '@tabler/icons-react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

export type PointRallyCourseCardProps = {
  /** コース名 */
  courseName: string
  /** 獲得済みポイント数 */
  earnedPoints: number
  /** 「獲得履歴を表示する」ボタン押下時のコールバック */
  onClickHistory?: () => void
  /** 購入連動アクセス制御によるロック（#3281）。true のとき灰色オーバーレイ＋鍵で操作を無効化し、ポイントを「-」表示にする */
  locked?: boolean
  /** ロック（locked）時のオーバーレイ押下で呼ばれる。解除商品の購入導線に使用する */
  onPurchaseUnlock?: () => void
}

/** ポイントラリー コースカード（Figma: 455:12661） */
export const PointRallyCourseCard: FC<PointRallyCourseCardProps> = ({
  courseName,
  earnedPoints,
  onClickHistory,
  locked = false,
  onPurchaseUnlock,
}) => {
  const { t } = useTranslation()
  return (
    <div className="w-full bg-white border border-[#cbcbce] rounded-[8px] relative overflow-hidden">
      {/* ロックオーバーレイ: 灰色透明＋中央に鍵。押下で解除商品の購入導線を起動する */}
      {locked && (
        <button
          type="button"
          onClick={onPurchaseUnlock}
          aria-label={t('home.purchase_unlock.aria', '購入して解除する')}
          className="absolute inset-0 z-20 flex w-full items-center justify-center rounded-[8px] bg-gray-500/50"
        >
          <IconLock size={48} className="text-white" aria-hidden="true" />
        </button>
      )}

      <div className="flex flex-col px-4 py-6">
        {/* コース名 */}
        <Typography variant="body4" component="p" className="text-center">
          {courseName}
        </Typography>

        {/* ポイント表示（ロック時は「-」）。数値と単位の間は gap で空け、items-end で下揃えにする。
            flex-1 を外して高さを内容（数値の行ボックス）ぴったりにし、上下に 8px の余白を取る */}
        <p className="my-[8px] flex items-end justify-end gap-[8px] text-black leading-none">
          <Typography variant="h1" component="span">
            {locked ? '- ' : earnedPoints}
          </Typography>
          <Typography variant="body3" component="span">
            {t('common.point', 'ポイント')}
          </Typography>
        </p>

        {/* 獲得履歴ボタン。カード幅いっぱい・白抜き（primaryOutlined）配色・高さは Button の既定サイズ md = 48px（#2970 で白抜きに変更） */}
        <Button
          label={t('home.show_point_history', '獲得履歴を表示する')}
          variant="primaryOutlined"
          fullWidth
          onClick={onClickHistory}
        />
      </div>
    </div>
  )
}
